import React, { useMemo, useState } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { useAppSelector } from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import useAdvancedSearchData from 'src/serverProxy/useAdvancedSearchData';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import _capitalize from 'lodash/capitalize';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useLatestCumulativeReviewWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';

const reviewPeriodOptions = [{ id: 'lifetime', label: 'Cumulative' }];

const mainMetricOptions = [
  { id: 'ratingVolume', label: 'Rating Volume' },
  { id: 'rating', label: 'Rating' }
];

const fields = {
  lifetime: {
    ratingVolume: [{ name: RatingsAndReviewMetric.CHILD_LIFETIME_RATING_VOLUME }],
    rating: [
      { name: RatingsAndReviewMetric.CHILD_LIFETIME_RATING_VOLUME },
      { name: RatingsAndReviewMetric.WEIGHTED_CHILD_LIFETIME_RATING },
      { name: RatingsAndReviewMetric.CUMULATIVE_REVIEW_RATING }
    ]
  }
};

const responseKeyMap = {
  ratingVolume: 'RatingVolume_sum',
  rating: 'Rating_computed'
};

enum SubtitleDisplayName {
  rating = 'rating',
  ratingVolume = 'rating volume',
  lifetime = 'cumulative'
}

/**
 * Double Dropdown for Cumulative Rating/Rating Volume by Category/SubCategory graph for ratings tab.
 */

const ReviewRatingDoubleDropdownByCategoryChart = ({ groupByField }: { groupByField: string }) => {
  const theme = useStacklineTheme();
  const [selectedMainMetricField, setSelectedMainMetricField] = useState('ratingVolume');
  const [reviewPeriod, setReviewPeriod] = useState('lifetime');
  const normalizedReviewPeriod = reviewPeriodOptions.some((option) => option.id === reviewPeriod)
    ? reviewPeriod
    : 'lifetime';
  const shouldShowReviewPeriodDropdown = reviewPeriodOptions.length > 1;
  const selectedReviewPeriodLabel =
    reviewPeriodOptions.find((option) => option.id === normalizedReviewPeriod)?.label || reviewPeriodOptions[0].label;
  const groupByKey = groupByField === 'subCategoryId' ? 'subcategory' : 'category';
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();

  const { data, isLoading } = useAdvancedSearchData({
    fields: fields[normalizedReviewPeriod][selectedMainMetricField],
    groupByField,
    indexName: BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2,
    requestId: `Beacon-ratings-and-reviews-double-dropdown-by-${groupByField}`,
    shouldFetchComparison: false,
    requestBuilder(builder) {
      if (normalizedReviewPeriod === 'lifetime') {
        builder.modifyAggregation((aggBuilder) => {
          aggBuilder.replaceConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId);
          return aggBuilder;
        });
        builder.replaceConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId);
      }
      return builder;
    }
  });

  const parsedData = useMemo(() => {
    const currentData = [];
    const categoryData = [];

    data.forEach((datum) => {
      const categoryObj = datum?.metadata?.[groupByKey] ?? datum?.comparisonMetadata?.[groupByKey];

      const formattedCategoryObj = {
        name: categoryObj?.name,
        id: categoryObj?.id,
        type: categoryObj?.categoryType?.toLocaleLowerCase()
      };
      let responseKey = `${normalizedReviewPeriod}${responseKeyMap[selectedMainMetricField]}_value`;

      if (responseKey === 'lifetimeRatingVolume_sum_value') {
        responseKey = 'childLifetimeRatingVolume_sum_value';
      }

      const currentDatum = datum?.values?.[responseKey] ?? 0;
      if (categoryObj && categoryObj.name) {
        currentData.push([categoryObj.name, currentDatum]);
        categoryData.push(formattedCategoryObj);
      }
    });
    return { currentData, categoryData };
  }, [data, groupByKey, normalizedReviewPeriod, selectedMainMetricField]);

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        try {
          const label = `${_capitalize(SubtitleDisplayName[normalizedReviewPeriod])} ${_capitalize(
            SubtitleDisplayName[selectedMainMetricField]
          )}`;
          return saveSingleBarChartAsDelimitedData(parsedData.currentData, {
            fileTitle: `Rating and Reviews - ${label}`,
            metricLabel: label
          });
        } catch (error) {
          console.error(`Ratings and Review fail`);
        }
        return Promise.resolve();
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the ${SubtitleDisplayName[normalizedReviewPeriod]} ${SubtitleDisplayName[selectedMainMetricField]} for ${entityPhrase} `}
            {taxonomyText} in each {groupByKey}
            .
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      title={
        <Flex alignItems="center" gap={6}>
          {shouldShowReviewPeriodDropdown ? (
            <SlDropdownMenu
              buttonVariant="title"
              options={reviewPeriodOptions}
              selectedId={normalizedReviewPeriod}
              onChange={(option) => setReviewPeriod(option.id)}
              defaultLabel="Cumulative"
            />
          ) : (
            <Text variant="h4">{selectedReviewPeriodLabel}</Text>
          )}

          <SlDropdownMenu
            buttonVariant="title"
            options={mainMetricOptions}
            selectedId={selectedMainMetricField}
            onChange={(option) => setSelectedMainMetricField(option.id)}
            defaultLabel="Rating Volume"
          />
          <Text variant="h4">by {_capitalize(groupByKey)}</Text>
        </Flex>
      }
    >
      {isLoading ? (
        <TopEntityChartV2Loading disableLegendSkeleton />
      ) : (
        <TopEntityChartV2
          chartSeries={[
            {
              type: 'column',
              data: parsedData.currentData,
              categories: parsedData.categoryData,
              metricType: selectedMainMetricField === 'ratingVolume' ? METRICTYPE.VOLUME : METRICTYPE.DECIMAL
            }
          ]}
          seriesColors={[theme.colors.primary]}
        />
      )}
    </BeaconChartWithLegend>
  );
};

export default ReviewRatingDoubleDropdownByCategoryChart;
